/**
 * PlayRedirect — participant entry point.
 *
 * Route: /play (no simId)
 *
 * Checks if user has an active role assignment → redirects to /play/{simId}.
 * If no role → shows waiting screen. The App-level RoleAssignmentWatcher
 * handles the pop-up and redirect when a role is assigned.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'

export function PlayRedirect() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [hasRole, setHasRole] = useState(false)

  useEffect(() => {
    if (!user) return

    async function findActiveSim() {
      // Check if user has an assigned role in any non-template active sim
      const { data: roles } = await supabase
        .from('roles')
        .select('sim_run_id')
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .neq('sim_run_id', '00000000-0000-0000-0000-000000000001')
        .limit(1)

      if (roles && roles.length > 0) {
        // Verify the sim is active/pre_start
        const { data: sim } = await supabase
          .from('sim_runs')
          .select('id,status')
          .eq('id', roles[0].sim_run_id)
          .in('status', ['active', 'pre_start', 'setup', 'paused'])
          .limit(1)

        if (sim && sim.length > 0) {
          setHasRole(true)
          navigate(`/play/${sim[0].id}`, { replace: true })
          return
        }
      }

      // No role found — show waiting screen
      // RoleAssignmentWatcher (App-level) will handle redirect when role is assigned
      setLoading(false)
    }

    findActiveSim()
  }, [user, navigate])

  if (loading && !hasRole) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center">
        <div className="text-text-secondary font-body">Connecting...</div>
      </div>
    )
  }

  // Waiting state — no role assigned yet
  return (
    <div className="min-h-screen bg-base flex items-center justify-center px-4">
      <div className="bg-card border border-border rounded-xl p-8 max-w-md text-center">
        <h2 className="font-heading text-h2 text-text-primary mb-3">
          Welcome to Thucydides Trap
        </h2>
        <p className="font-body text-body-sm text-text-secondary mb-4">
          Your simulation is being prepared. You will be assigned a role shortly.
        </p>
        <p className="font-body text-caption text-text-secondary/60">
          This page will update automatically — no need to refresh.
        </p>
      </div>
    </div>
  )
}
